import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, ArrowRight, Share2, Copy, Check,
  Bell, BellRing, Hash, MapPin, Clock, Users,
  HelpCircle, Lock, Volume2,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/patient-log-live-dashboard';
const SHARE_MESSAGE = (
  `New on DocSite — your dashboard now alerts you live the moment patients land.\n\n` +
  `• Pulsing green dot + count badge on every tab when new entries arrive\n` +
  `• Audible chime on phone/desktop the second a new patient checks in or a new lead lands\n` +
  `• "Visit #" rank on every returning patient (across all your clinics)\n` +
  `• Records are now immutable — no accidental deletes, full audit trail forever\n` +
  `• Structured First / Middle / Surname capture — no more casual one-worders\n\n` +
  `Read more: ${SHARE_URL}`
);

const KEY_FEATURES = [
  {
    icon: BellRing,
    title: 'Pulsing green dot + daily count on every dashboard tab',
    body: 'A green pulsing dot lights up on the Patient Log, My Leads, Bookings, Voice Calls, Chats and Feedback tabs whenever new entries arrive. Clicking the tab dismisses the dot — but the count badge ("5", "12") stays visible all day so you always know the running tally. Auto-resets at local midnight.',
    color: 'emerald',
  },
  {
    icon: Volume2,
    title: 'Audible chime when a new patient checks in or new lead lands',
    body: 'Phone + desktop both ring. PWA-installed mobile vibrates and plays the OS notification sound. Browser tabs play a pleasant two-note chime (A5 → E6) — even when your system sounds are muted but the dashboard tab is open.',
    color: 'amber',
  },
  {
    icon: Hash,
    title: '"Visit #" rank on every returning patient',
    body: 'See instantly whether the patient in front of you is a first-timer ("#1"), a follow-up ("#2"), or a regular ("#7"). The count aggregates across ALL your clinics — so a patient who came to Clinic A on day 1 and Clinic B on day 5 sees rank 2 on the second row.',
    color: 'sky',
  },
  {
    icon: MapPin,
    title: '"Clinic" column on Patient Log',
    body: 'Each row now shows which exact clinic the patient visited — pulled straight from the per-clinic Check-In QR they scanned. No more guessing across multi-location practices.',
    color: 'sky',
  },
  {
    icon: Clock,
    title: 'Exact arrival time on every check-in (in your local time)',
    body: 'Patient Log rows + the "in clinic now" banner both show the precise HH:MM of the scan — now correctly rendered in your local timezone (the earlier UTC bug that made an 11:18 AM scan show as 05:48 is gone).',
    color: 'sky',
  },
  {
    icon: HelpCircle,
    title: 'Smart re-check-in: "?" badge + Accept/Reject — no duplicates',
    body: 'If a patient scans your QR a second time with fuller details (e.g. adds her surname or symptom), the system does NOT create a duplicate row. Instead a small "?" badge appears on the original record. One tap: Accept update (merges + audit-logs) or Keep current (discards). The patient never gets a second feedback SMS either.',
    color: 'amber',
  },
  {
    icon: Lock,
    title: 'Immutable patient records — name + phone locked, no deletes',
    body: 'Once a patient record lands, it can never be deleted. Notes and source/channel remain editable. Name + phone are read-only when the patient entered them on their own device (clinic QR, Connect, chat, voice call) — only manual "+ Log a visit" entries let you edit those fields. Stray scans are soft-marked, not deleted. Full audit trail forever.',
    color: 'rose',
  },
  {
    icon: Users,
    title: 'Structured First / Middle / Surname capture on every patient surface',
    body: 'Clinic Check-In, Connect page, voice call request and chat all now ask for First (required) + Middle (optional) + Surname (please share) — gently nudging patients to give you their full proper name. Returning patients get their three fields pre-filled. No more "Patient First name" on its own in your CRM.',
    color: 'sky',
  },
];

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
  amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200' },
  sky: { bg: 'bg-sky-50', icon: 'text-sky-600', border: 'border-sky-200' },
  rose: { bg: 'bg-rose-50', icon: 'text-rose-600', border: 'border-rose-200' },
  slate: { bg: 'bg-slate-50', icon: 'text-slate-600', border: 'border-slate-200' },
};

export default function PatientLogLiveDashboardLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — Patient Log v2.5', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="patient-log-live-launch-page">
      <Helmet>
        <title>Patient Log v2.5 — live alerts, audible chimes, immutable records | DocSite</title>
        <meta name="description" content="Pulsing green dot + count badge on every tab, audible chime on phone/desktop for new patients & leads, Visit-# rank on returning patients, immutable records. The doctor's dashboard now alerts you live the second something happens." />
        <meta property="og:title" content="DocSite — Patient Log v2.5: Live dashboard intelligence" />
        <meta property="og:description" content="Live alerts, audible chimes, Visit-# rank, immutable records. Your dashboard now wakes you up the moment a patient walks in." />
        <meta property="og:url" content={SHARE_URL} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-6" data-testid="back-to-whats-new">
          <ArrowLeft size={12} /> All updates
        </Link>

        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-200 p-6 sm:p-10 mb-6">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-600 text-white">
            <Sparkles size={10} /> Just Launched · 16 Feb 2026
          </span>
          {/* "WAKE UP" bell — the signature mark for this rollout. A pulsing
              halo + a single emerald bell-ringing icon visually anchors the
              whole "your dashboard now wakes you up" narrative. */}
          <div className="mt-5 flex items-center gap-4">
            <div className="relative shrink-0">
              <span className="absolute inset-0 rounded-2xl bg-emerald-400/30 animate-ping" aria-hidden="true"></span>
              <span className="relative inline-flex w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-lg items-center justify-center" data-testid="wakeup-bell">
                <BellRing size={36} className="text-white sm:hidden" strokeWidth={2} />
                <BellRing size={44} className="text-white hidden sm:block" strokeWidth={2} />
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700/80">Wake-up rollout</p>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Live alerts · Audible chime · Immutable records</p>
            </div>
          </div>
          <h1 className="text-2xl sm:text-4xl font-semibold text-slate-900 mt-5 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your dashboard now wakes you up the second a patient walks in.
          </h1>
          <p className="mt-3 text-base sm:text-lg text-slate-700 leading-relaxed">
            Pulsing green dot + count badge on every tab. Audible chime on phone & desktop.
            "Visit #" rank on every returning patient across all your clinics. And patient records
            are now <strong className="text-slate-900">immutable</strong> — once a row lands, it stays forever, name + phone
            locked because the patient entered them, only notes/source editable.
          </p>
        </div>

        {/* Key features list */}
        <div className="space-y-3">
          {KEY_FEATURES.map((f, idx) => {
            const c = COLOR_MAP[f.color] || COLOR_MAP.sky;
            const Ico = f.icon;
            return (
              <div key={idx} className={`rounded-2xl bg-white border ${c.border} p-5 flex gap-4 items-start hover:shadow-sm transition-shadow`} data-testid={`feature-${idx}`}>
                <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center shrink-0`}>
                  <Ico size={20} className={c.icon} strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {f.title}
                  </h3>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.body}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-5">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2 flex items-center gap-1.5">
            <Bell size={12} /> Why it matters
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">
            You can't afford to keep checking your dashboard manually. DocSite now does the watching for you —
            on the desk, in your hand, even when the system is silent. Returning patients are recognised on sight.
            Mistakes can't erase a record. Casual one-word names are gently turned into full proper names.
            <span className="font-semibold text-slate-900"> Less typing. More signal. More trust.</span>
          </p>
        </div>

        {/* Share strip */}
        <div className="mt-8 rounded-2xl bg-slate-900 text-white p-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <p className="text-sm flex-1">Share this with a doctor friend who's still chasing leads by hand.</p>
          <div className="flex gap-2 flex-wrap">
            <a href={whatsappHref} target="_blank" rel="noreferrer"
               className="px-3.5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold flex items-center gap-1.5"
               data-testid="share-whatsapp">
              <Share2 size={14} /> WhatsApp
            </a>
            <button onClick={nativeShare}
                    className="px-3.5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold flex items-center gap-1.5"
                    data-testid="share-native">
              <Share2 size={14} /> Share
            </button>
            <button onClick={copyLink}
                    className="px-3.5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold flex items-center gap-1.5"
                    data-testid="share-copy">
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </div>

        {/* CTA back */}
        <div className="mt-8 text-center">
          <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-900">
            See all DocSite updates <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
